/**
 * useSelection - Hook for managing multi-select state
 * Used for bulk operations in admin tables
 */

import { useState, useCallback, useMemo } from 'react';

export interface UseSelectionReturn {
  selectedIds: Set<string>;
  isSelected: (id: string) => boolean;
  toggleSelection: (id: string) => void;
  toggleAll: (allIds: string[]) => void;
  clearSelection: () => void;
  selectedCount: number;
  allSelected: (allIds: string[]) => boolean;
}

/**
 * Hook for managing selection state in tables
 * @returns Selection state and handlers
 */
export function useSelection(): UseSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isSelected = useCallback(
    (id: string): boolean => {
      return selectedIds.has(id);
    },
    [selectedIds]
  );

  const toggleSelection = useCallback((id: string): void => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const toggleAll = useCallback((allIds: string[]): void => {
    setSelectedIds((prev) => {
      const allCurrentlySelected = allIds.every((id) => prev.has(id));

      if (allCurrentlySelected) {
        // Deselect all
        const newSet = new Set(prev);
        allIds.forEach((id) => newSet.delete(id));
        return newSet;
      } else {
        // Select all
        const newSet = new Set(prev);
        allIds.forEach((id) => newSet.add(id));
        return newSet;
      }
    });
  }, []);

  const clearSelection = useCallback((): void => {
    setSelectedIds(new Set());
  }, []);

  const allSelected = useCallback(
    (allIds: string[]): boolean => {
      if (allIds.length === 0) return false;
      return allIds.every((id) => selectedIds.has(id));
    },
    [selectedIds]
  );

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  return {
    selectedIds,
    isSelected,
    toggleSelection,
    toggleAll,
    clearSelection,
    selectedCount,
    allSelected,
  };
}
